import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Button } from '@teuteuf-games/button';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AdnginInContent } from '../../components/AdnginInContent';
import { BackButton } from '../../components/buttons/BackButton';
import { Spinner } from '../../components/spinner/spinner';
import { useUser } from '../../providers/UserProvider';
import { MatchBoard } from './MatchBoard';
import { MatchModeConfig } from './matchConfig';
import { MatchEndScreen } from './MatchEndScreen';
import { SharedItemModal } from './SharedItemModal';
import { readSharedItemId } from './shareLink';
import { SESSION_ROUNDS, Tally, useMatchSession } from './useMatchSession';

/** Labelled running stats, inline as in the mockup. */
const SessionStats = ({
  correct,
  total,
  i18nBase,
}: Tally & { i18nBase: string }) => {
  const { t } = useTranslation();
  const accuracy = total ? Math.round((correct / total) * 100) : 0;

  return (
    <div className="flex w-full py-0.5 gap-2 items-center justify-center text-xs md:text-sm">
      <span className="flex gap-1 items-center">
        <CheckCircleRoundedIcon
          className="text-[#289535]"
          style={{ fontSize: 16 }}
        />
        <span className="font-bold">{correct}</span>
        <span className="text-gray-500 dark:text-slate-400">
          {t(`${i18nBase}.correct`)}
        </span>
      </span>

      <span className="h-3.5 w-px bg-gray-300 dark:bg-slate-600" />

      <span className="flex gap-1 items-center">
        <CancelRoundedIcon
          className="text-[#CC0A1C]"
          style={{ fontSize: 16 }}
        />
        <span className="font-bold">{total - correct}</span>
        <span className="text-gray-500 dark:text-slate-400">
          {t(`${i18nBase}.wrong`)}
        </span>
      </span>

      <span className="text-gray-400 dark:text-slate-500" aria-hidden="true">
        ·
      </span>

      <span className="flex gap-1 items-center">
        <span className="font-bold">{accuracy}%</span>
        <span className="text-gray-500 dark:text-slate-400">
          {t('modes.accuracy')}
        </span>
      </span>
    </div>
  );
};

export const MatchGame = ({ config }: { config: MatchModeConfig }) => {
  const { t } = useTranslation();
  const { userDetails } = useUser();
  const i18nBase = config.i18nBase;

  /*
    Someone arriving from a shared link lands on /city-flags#img-420. It is read
    once on mount; dismissing clears the fragment so a refresh starts the game
    rather than reopening the flag.
  */
  const [sharedItemId, setSharedItemId] = useState(() =>
    readSharedItemId(window.location.hash, config),
  );

  const dismissSharedItem = () => {
    setSharedItemId(null);
    window.history.replaceState(null, '', window.location.pathname);
  };

  const {
    round,
    tally,
    isFinished,
    isComplete,
    isLoading,
    loadFailed,
    placeFlag,
    clearSlot,
    check,
    advance,
    restart,
  } = useMatchSession(config);

  if (loadFailed) {
    return (
      <div className="flex flex-col w-full px-3 gap-4 items-center text-center">
        <p className="mt-6">{t('modes.load-failed')}</p>
        <BackButton />
      </div>
    );
  }

  if (isFinished) {
    return (
      <MatchEndScreen config={config} tally={tally} onPlayAgain={restart} />
    );
  }

  if (isLoading || !round) {
    return (
      <div className="flex w-full min-h-[300px] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const isResolved = round.phase === 'resolved';

  return (
    <div className="flex flex-col w-full max-w-lg px-2 md:px-3 pt-[10px] gap-1.5 items-center">
      <div className="flex w-full items-center justify-between gap-2">
        <BackButton />
        <p className="flex-1 text-center font-bold text-sm md:text-lg">
          {t(`${i18nBase}.instruction`)}
        </p>
        <p className="text-xs md:text-sm font-bold text-gray-500 dark:text-slate-300 whitespace-nowrap">
          {t(`${i18nBase}.round`, {
            current: round.index + 1,
            total: SESSION_ROUNDS,
          })}
        </p>
      </div>

      <SessionStats
        correct={tally.correct}
        total={tally.total}
        i18nBase={i18nBase}
      />

      {/* Breathing room between the stats bar and the board. */}
      <div className="w-full mt-2">
        <MatchBoard
          config={config}
          round={round}
          onPlace={placeFlag}
          onClear={clearSlot}
          onAdvance={advance}
        />
      </div>

      <div className="flex w-full mt-1">
        {isResolved ? (
          <Button
            buttonStyle="primary"
            label={t(`${i18nBase}.continue`)}
            width="w-full"
            onClick={advance}
          />
        ) : (
          <Button
            buttonStyle="primary"
            label={t(`${i18nBase}.check`)}
            width="w-full"
            disabled={!isComplete || round.phase !== 'placing'}
            onClick={check}
          />
        )}
      </div>

      {!userDetails?.premiumGames?.includes('flagle') && <AdnginInContent />}

      {sharedItemId && (
        <SharedItemModal
          config={config}
          itemId={sharedItemId}
          onClose={dismissSharedItem}
        />
      )}
    </div>
  );
};
